<template>
    <div class="pop_overlay_black" v-if="isShow">
        <div class="pop_wrap rsv2_manager_set_pop" style="width: 940px; height: 640px;">
            <div v-if="isLoading" class="loading_bg round transparent" style="z-index: 999;">
                <div class="loading_wrap">
                    <div class="loading_obj">
                        <svg class="spinner" width="60px" height="60px" viewBox="0 0 66 66"
                             xmlns="http://www.w3.org/2000/svg">
                            <circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33"
                                    r="30">
                            </circle>
                        </svg>
                    </div>
                </div>
            </div>
            <div class="pop_title">
                <span class="title">{{ $t('popup_notice.title') }}</span>
                <button class="mgl_auto" @click="action_ClosePopup">
                    <v-icon large>mdi-close</v-icon>
                </button>
            </div>
            <div class="pop_memo">
                <div class="pop_memo_left">
                    <v-date-picker
                        :locale="hospitalLocale" color="#1F8FE5" ref="datepicker"
                        v-model="selectedDate"
                        :day-format="date => new Date(date).getDate()"
                        :events="dateNotes"
                        @update:picker-date="updatePickerMonth"
                        :event-color="'#FEC860'">
                    </v-date-picker>
                </div>
                <div class="pop_memo_right">
                    <b class="lgray_txt">{{ $t('popup_notice.content') }}</b>
                    <textarea
                        class="input_default pop_memo_input mgt_5"
                        :placeholder="$t('popup_notice.placeholder_content')"
                        v-model="content"
                        maxlength="5000"
                        :disabled="!isEditing"
                    />
                    <table class="table2_default small mgt_15 no_hover">
                        <tr>
                            <th style="width: 100px">{{ $t('popup_notice.label_last_modified') }}</th>
                            <td>{{ getUpdatedAt }} / {{ getModifierName }}</td>
                        </tr>
                    </table>
                    <div class="btn_wrap">
                        <button class="btn btn_l btn_blue_line" v-if="!isEditing"
                                @click="action_Edit">{{ $t('common.edit') }}
                        </button>
                        <button class="btn btn_l btn_blue" v-if="isEditing"
                                @click="requestUpdateReservationDateNote">{{ $t('common.save') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import moment from '@/util/MPMoment'
import { closeReservationDateNotePopup, sheetOverlays } from '../../../sheet-overlays'
import { sheetAxis } from '../../../sheet-axis'
import { handleDateNoteRefresh } from '../../../sheet-refresh'
import { useQueryClient } from '@tanstack/vue-query'
import { v2ReservationDateNoteFindDateQueryOptions } from '@/composables/queries/v2reservationdatenote/use-v2-reservation-datenote-find-date-query'
import { useV2ReservationDateNoteUpdateMutation } from '@/composables/queries/v2reservationdatenote/use-v2-reservation-datenote-update-mutation'
import { v2ReservationDateNoteFindQueryOptions } from '@/composables/queries/v2reservationdatenote/use-v2-reservation-datenote-find-query'

export default {
    setup() {
        // 팝업 이벤트 기반 명령형(await) 조회라 useQuery 대신 queryClient.fetchQuery 를 쓴다
        const queryClient = useQueryClient()
        const { mutateAsync: updateReservationDateNote } = useV2ReservationDateNoteUpdateMutation()
        return { queryClient, updateReservationDateNote }
    },
    data() {
        return {
            sheetOverlays,
            sheetAxis,
            dateNotes: [],
            selectedDate: '',
            pickerCurrentMonth: '',

            originalNote: undefined,
            content: '',
            isEditing: false,
            isLoading: false
        }
    },
    watch: {
        isShow: {
            handler: function (newValue) {
                if (newValue) {
                    this.pickerCurrentMonth = moment(this.sheetAxis.selectedDate).format("YYYY-MM")
                    this.selectedDate = moment(this.sheetAxis.selectedDate).format("YYYY-MM-DD")
                    document.addEventListener('keyup', this.handleKeyupEvent)
                } else {
                    this.pickerCurrentMonth = ''
                    this.selectedDate = ''
                    document.removeEventListener('keyup', this.handleKeyupEvent)
                }
            }
        },
        pickerCurrentMonth: {
            handler: function (newValue) {
                if ((newValue ?? '') != '') {
                    this.requestFetchSelectedMonthDateNotes()
                }
            }
        },
        selectedDate: {
            handler: function (newValue) {
                if ((newValue ?? '') != '') {
                    this.isEditing = false
                    this.requestFetchReservationDateNote()
                }
            }
        },
        originalNote: {
            handler: function (newValue) {
                this.content = (newValue?.content ?? '')
            }
        }
    },
    computed: {
        isShow: function () {
            return this.sheetOverlays.isShowReservationDateNotePopup
        },
        isNew: function () {
            return this.originalNote == undefined
        },
        getUpdatedAt() {
            if (this.originalNote?.modifierID == undefined) {
                return '-'
            }
            return moment(this.originalNote?.updatedAt).format(this.$t('common.date_format_YYYY_MM_DD_HH_mm'))
        },
        getModifierName() {
            return this.originalNote?.modifierID?.realName ?? '-'
        }
    },
    methods: {
        updatePickerMonth(date) {
            this.pickerCurrentMonth = date
        },
        action_ClosePopup() {
            closeReservationDateNotePopup()
        },
        action_Edit() {
            this.isEditing = true
        },

        handleKeyupEvent(event) {
            if (event.keyCode == 27) {

                this.action_ClosePopup()
            }
        },

        // MARK: - Network
        // 기간 내 공지사항 목록 조회
        async requestFetchSelectedMonthDateNotes() {
            try {
                const year = this.pickerCurrentMonth.split('-')[0]
                const month = this.pickerCurrentMonth.split('-')[1]
                let m = moment().set('year', year).set('month', month-1)
                let param = {
                    hospitalID: this.hospital.selectedHospital._id,
                    startDate: m.startOf('month').format('YYYY-MM-DD'),
                    endDate: m.endOf('month').format('YYYY-MM-DD'),
                    key: 'date',
                    way: 1
                }
                // 봉투 에러(code != 0)는 ApiError로 throw — 아래 catch에서 로그 (기존 동작 동일)
                const notes = await this.queryClient.fetchQuery(v2ReservationDateNoteFindDateQueryOptions(param))
                this.dateNotes = [...new Set(
                    notes
                        .filter(note => {
                            return !note.content == false
                        })
                        .map(note => {
                            return moment(note.date).format('YYYY-MM-DD')
                        })
                )]
            } catch (err) {
                console.log("reservation date note find error : ", err)
            }
        },
        // 공지사항 단건 조회
        async requestFetchReservationDateNote() {
            try {
                this.isLoading = true
                let param = {
                    hospitalID: this.hospital.selectedHospital._id,
                    date: this.selectedDate
                }
                const data = await this.queryClient.fetchQuery(v2ReservationDateNoteFindQueryOptions(param))
                this.originalNote = data?.[0]
            } catch (err) {
                console.log('reservation date note find error: ', err)
            } finally {
                this.isLoading = false
            }
        },
        // 공지사항 등록/수정
        async requestUpdateReservationDateNote() {
            try {
                this.isLoading = true
                let param = {
                    hospitalID: this.hospital.selectedHospital._id,
                    date: this.selectedDate,
                    content: this.content
                }
                // 봉투 에러(code != 0)는 ApiError로 throw — 아래 catch에서 로그 (기존 동작 동일)
                const updated = await this.updateReservationDateNote(param)
                let selectedSheetDate = moment(this.sheetAxis.selectedDate).format("YYYY-MM-DD")
                if (this.selectedDate == selectedSheetDate) {
                    handleDateNoteRefresh()
                }
                this.originalNote = updated
                this.isEditing = false
                await this.requestFetchSelectedMonthDateNotes()
            } catch (err) {
                console.log('reservation date note update error: ', err)
            } finally {
                this.isLoading = false
            }
        }
    }
}
</script>